import { memo, useMemo } from 'react';
import { Tab } from './state';
import { MultitabRouter } from './Router';
import { OutletContext } from './context';
import { getPathList, getRouteList } from './share';
import { NavigateWithQuery } from 'hooks/useNavigateWithQuery';
import { useMemoizedFn } from 'ahooks';
import { usePermissionRouteConfig } from 'components/Router/context';

export interface PageProps {
  tab: Tab;
  active: boolean;
  elementsFilter?: (elements: JSX.Element[]) => JSX.Element[];
}

const Page = memo(({ tab, active, elementsFilter }: PageProps) => {
  const { path } = tab;
  const { mainRoutes } = usePermissionRouteConfig();

  const memoElementsFilter = useMemoizedFn((elements: JSX.Element[]) =>
    elementsFilter ? elementsFilter(elements) : elements,
  );

  const { content, params, redirect } = useMemo(() => {
    const routeList = getRouteList(mainRoutes ?? [], path);

    const matchedPathList = routeList.reduce<string[]>((res, { path }) => {
      return [...res, ...path.split('/').filter((_) => _)];
    }, []);

    const params = matchedPathList.reduce<Record<string, string>>((res, cur, index) => {
      if (cur.startsWith(':')) {
        res[cur.replaceAll(':', '').replaceAll('?', '')] = getPathList(path)[index];
      }
      return res;
    }, {});

    const render = (elements: JSX.Element[]) => {
      if (elements.length === 0) {
        return <></>;
      }

      return (
        <OutletContext.Provider value={{ outlet: render(elements.slice(1)) }}>
          {elements[0]}
        </OutletContext.Provider>
      );
    };

    const content = render(
      memoElementsFilter(routeList.map((_) => _.element).filter((_) => _) as any),
    );

    const route = routeList[routeList.length - 1];
    const redirect =
      typeof route.redirect === 'string'
        ? route.redirect
        : route.redirect && route.children?.length
        ? // Page 手动渲染子路由，React Router 的相对基准仍可能是工作台父路由。
          // 默认子页必须基于当前 tab 的完整路径，避免 refund/return 丢失 refund。
          route.children[0].path.startsWith('/')
          ? route.children[0].path
          : `${path.replace(/\/$/, '')}/${route.children[0].path}`
        : '';

    return {
      params,
      content,
      redirect,
    };
  }, [path, mainRoutes, memoElementsFilter]);

  return (
    <section style={active ? undefined : { display: 'none' }}>
      <MultitabRouter tab={tab} params={params}>
        {content}
        {redirect ? <NavigateWithQuery replace key={redirect} to={redirect} /> : null}
      </MultitabRouter>
    </section>
  );
});

export default Page;
